import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Star, MapPin, Loader2, X } from "lucide-react";

const EMPTY = { label: "", address_line1: "", address_line2: "", city: "", postcode: "", notes: "", is_default: false };

export default function AddressesManager() {
  const { user, refresh } = useAuth();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState({});

  const addresses = user?.addresses || [];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address_line1 || !form.city || !form.postcode) {
      toast.error("Address line 1, city and postcode are required."); return;
    }
    setSaving(true);
    try {
      // Validate postcode in zone
      const { data: pc } = await api.post("/postcode/validate", { postcode: form.postcode });
      if (!pc.valid) { toast.error(pc.reason || "Postcode out of zone."); setSaving(false); return; }
      await api.post("/auth/me/addresses", form);
      toast.success("Address saved");
      setForm(EMPTY); setAdding(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save address.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    setRemoving((r) => ({ ...r, [id]: true }));
    try {
      await api.delete(`/auth/me/addresses/${id}`);
      toast.success("Address removed");
      await refresh();
    } catch {
      toast.error("Could not remove address.");
    } finally {
      setRemoving((r) => ({ ...r, [id]: false }));
    }
  };

  const setDefault = async (a) => {
    try {
      await api.patch(`/auth/me/addresses/${a.id}`, { ...a, is_default: true });
      await refresh();
      toast.success("Default address updated");
    } catch {
      toast.error("Could not update default.");
    }
  };

  return (
    <section className="poster-card p-6" data-testid="addresses-manager">
      <div className="flex items-center justify-between">
        <div className="font-heading uppercase tracking-tight text-cream text-2xl">Saved Addresses</div>
        <div className="text-cream/50 text-xs font-heading uppercase tracking-widest">{addresses.length} / 8</div>
      </div>

      {addresses.length === 0 && !adding && (
        <div className="mt-3 text-cream/60 text-sm" data-testid="addresses-empty-msg">
          No saved addresses yet — save one at checkout, or add now.
        </div>
      )}

      {addresses.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="addresses-list">
          {addresses.map((a) => (
            <li key={a.id} className="border border-border p-3 flex items-start justify-between gap-3" data-testid={`address-row-${a.id}`}>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gold mt-1 shrink-0" />
                <div>
                  <div className="font-heading uppercase tracking-tight text-cream text-lg leading-none flex items-center gap-2">
                    {a.label || "Address"}
                    {a.is_default && <span className="text-gold text-[10px] tracking-widest font-heading">DEFAULT</span>}
                  </div>
                  <div className="text-cream/70 text-sm mt-1">
                    {a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ""}, {a.city} {a.postcode}
                  </div>
                  {a.notes && <div className="text-cream/50 text-xs italic mt-1">"{a.notes}"</div>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!a.is_default && (
                  <button onClick={() => setDefault(a)} className="px-2 py-1 border border-border text-cream/60 hover:text-gold hover:border-gold font-heading uppercase tracking-widest text-[10px]" data-testid={`set-default-${a.id}`}>
                    <Star className="h-3 w-3 inline mr-1" /> Default
                  </button>
                )}
                <button
                  onClick={() => remove(a.id)} disabled={removing[a.id]}
                  className="text-cream/40 hover:text-accent p-1"
                  data-testid={`remove-address-${a.id}`}
                >
                  {removing[a.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {!adding && addresses.length < 8 && (
        <button
          onClick={() => setAdding(true)}
          className="mt-4 inline-flex items-center gap-2 btn-outline-poster"
          data-testid="add-address-btn"
        >
          <Plus className="h-4 w-4" /> Add Address
        </button>
      )}

      {adding && (
        <form onSubmit={submit} className="mt-4 border border-gold/40 p-4 bg-gold/5 space-y-3" data-testid="add-address-form">
          <div className="flex items-center justify-between">
            <div className="font-heading uppercase tracking-widest text-gold text-sm">New Address</div>
            <button type="button" onClick={() => { setAdding(false); setForm(EMPTY); }} className="text-cream/60 hover:text-cream">
              <X className="h-4 w-4" />
            </button>
          </div>
          <Field label="Label" value={form.label} onChange={(v) => set("label", v)} testid="addr-label-input" placeholder="Home / Work / Mum's" />
          <Field label="Address Line 1 *" value={form.address_line1} onChange={(v) => set("address_line1", v)} testid="addr-line1-input" />
          <Field label="Address Line 2" value={form.address_line2} onChange={(v) => set("address_line2", v)} testid="addr-line2-input" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="City *" value={form.city} onChange={(v) => set("city", v)} testid="addr-city-input" />
            <Field label="Postcode *" value={form.postcode} onChange={(v) => set("postcode", v.toUpperCase())} testid="addr-postcode-input" />
          </div>
          <Field label="Delivery notes" value={form.notes} onChange={(v) => set("notes", v)} testid="addr-notes-input" />
          <label className="inline-flex items-center gap-2 cursor-pointer text-cream text-sm">
            <input type="checkbox" checked={form.is_default} onChange={(e) => set("is_default", e.target.checked)} className="accent-amber-500 h-4 w-4" data-testid="addr-default-checkbox" />
            <span className="font-heading uppercase tracking-widest text-xs">Set as default</span>
          </label>
          <button type="submit" disabled={saving} className="btn-poster !text-sm !px-4 !py-2" data-testid="addr-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Address"}
          </button>
        </form>
      )}
    </section>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="font-heading uppercase tracking-widest text-xs text-cream/70">{label}</label>
      <input
        type="text" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-transparent border-2 border-border focus:border-gold outline-none px-3 py-2 text-cream"
        data-testid={testid}
      />
    </div>
  );
}
